import { ReactNode } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { ShowDetails } from "~/components/ShowDetails/ShowDetails";
import { Flex } from "~/components/Flex";
import {
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { formatDistanceToNowStrict } from "date-fns";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { ScopeType } from "~/hooks/useScope";

interface DashboardHeaderProps {
  context: ScopeType;
  title: ReactNode | string;
  description?: ReactNode | string;
  additionalContent?: ReactNode;
  isRefreshing?: boolean;
  lastRefreshed?: string;
  onRefresh?: () => void;
}

export const HomeDashboardHeader = ({
  context,
  title,
  description,
  additionalContent,
  isRefreshing,
  lastRefreshed,
  onRefresh,
}: DashboardHeaderProps) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");
  const isNewOrgDashboardEnabled = isFeatureEnabled("New Org Dashboard");
  const showRefreshUI =
    isMoveTheNeedleFeatureEnabled &&
    (context === ScopeType.Space ||
      (context === ScopeType.Organization && isNewOrgDashboardEnabled));

  return (
    <Box sx={{ mb: 3 }}>
      <Grid size={12}>
        <Flex justifyContent="space-between" alignItems="center">
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: "bold", textTransform: "uppercase" }}
          >
            {title}
          </Typography>
        </Flex>
      </Grid>

      {showRefreshUI && (
        <Flex
          sx={{
            color: "text.secondary",
            alignItems: "center",
            gap: 0.5,
            mt: 1,
          }}
        >
          {!isRefreshing ? (
            <AccessTimeIcon fontSize="inherit" />
          ) : (
            <CircularProgress size={13} sx={{ color: "text.secondary" }} />
          )}

          <Typography variant="caption">
            <strong>{isRefreshing ? "Updating ..." : "Last Updated:"}</strong>{" "}
            {!isRefreshing && (
              <>
                {lastRefreshed
                  ? `${formatDistanceToNowStrict(lastRefreshed)} ago`
                  : "Never"}
              </>
            )}
          </Typography>

          {!isRefreshing && (
            <RefreshIcon
              fontSize="inherit"
              onClick={onRefresh}
              sx={{ cursor: "pointer" }}
            />
          )}
        </Flex>
      )}

      <Box sx={{ color: "text.secondary", mt: 1 }}>
        <ShowDetails
          children={description}
          additionalContent={additionalContent}
        />
      </Box>
    </Box>
  );
};
